import React from "react";
import { Bell, CheckCircle2, Clock3, ExternalLink, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { notificationService } from "../../services/notificationService";
import { studentRequestService } from "../../services/studentRequestService";
import poeService from "../../services/poeService";

const readRoles = (profile, user) => {
  const secondary = Array.isArray(profile?.secondary_roles)
    ? profile.secondary_roles
    : typeof profile?.secondary_roles === "string"
      ? (() => { try { const parsed = JSON.parse(profile.secondary_roles); return Array.isArray(parsed) ? parsed : profile.secondary_roles.split(","); } catch (_) { return profile.secondary_roles.split(","); } })()
      : [];
  return [
    profile?.primary_role,
    ...secondary,
    user?.user_metadata?.role,
    user?.user_metadata?.primary_role,
    user?.app_metadata?.role,
    user?.app_metadata?.primary_role,
  ].map((role) => String(role || "").trim().toLowerCase().replace(/[\s-]+/g, "_")).filter(Boolean);
};

const reviewerStage = (roles) => {
  if (roles.some((role) => ["system_administrator", "chief_principal", "vice_chancellor", "vc", "principal"].includes(role))) return "all";
  if (roles.includes("head_of_department") || roles.includes("head_of_section")) return "hod_review";
  if (roles.includes("registrar_academic") || roles.includes("deputy_principal_academic")) return "academic_review";
  if (roles.includes("admissions_officer")) return "admissions_review";
  if (roles.includes("finance_officer")) return "finance_review";
  return null;
};

const hasPoeRole = (roles) => roles.some((role) => [
  "student",
  "trainer_lecturer",
  "head_of_department",
  "head_of_section",
  "poe_manager",
  "assessment_officer",
  "quality_assurance_officer",
  "external_poe_verifier",
].includes(role));

const relativeTime = (value) => {
  if (!value) return "";
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const normalizeNotification = (notification) => ({
  id: notification.id,
  title: notification.title || "Notification",
  message: notification.message || "You have an item requiring attention.",
  type: notification.notification_type || "info",
  actionUrl: notification.action_url || "/student-requests",
  createdAt: notification.created_at || new Date().toISOString(),
});

const WIDGET_POSITION_KEY = "student-portal.notification-widget-position";

const readSavedPosition = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(WIDGET_POSITION_KEY) || "null");
    if (Number.isFinite(saved?.left) && Number.isFinite(saved?.top)) return saved;
  } catch (_) {
    // Ignore unavailable or malformed local storage and use the default corner.
  }
  return null;
};

const FloatingNotificationWidget = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [position, setPosition] = React.useState(readSavedPosition);
  const widgetRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const suppressClickRef = React.useRef(false);
  const dismissedNotificationIdsRef = React.useRef(new Set());

  React.useEffect(() => {
    dismissedNotificationIdsRef.current = new Set();
  }, [user?.id]);

  const clampPosition = React.useCallback((left, top) => {
    const rect = widgetRef.current?.getBoundingClientRect();
    const width = rect?.width || 48;
    const height = rect?.height || 48;
    return {
      left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(top, window.innerHeight - height - 8)),
    };
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;
    const start = position || { left: rect.left, top: rect.top };
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: start.left,
      startTop: start.top,
      lastPosition: start,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < 5) return;
    drag.moved = true;
    const next = clampPosition(drag.startLeft + deltaX, drag.startTop + deltaY);
    drag.lastPosition = next;
    setPosition(next);
  };

  const finishPointer = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.moved) {
      suppressClickRef.current = true;
      try { window.localStorage.setItem(WIDGET_POSITION_KEY, JSON.stringify(drag.lastPosition)); } catch (_) { /* Ignore storage errors. */ }
    }
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
  };

  const load = React.useCallback(async () => {
    if (!user?.id || authLoading) return;
    setLoading(true);
    try {
      const roles = readRoles(profile, user);
      const generic = await notificationService.listUnread(user.id).catch(() => []);
      const derived = [];
      const stage = reviewerStage(roles);

      if (stage) {
        const [queue, departmentNotes] = await Promise.all([
          studentRequestService.listQueue(stage, profile?.department_id, user.id, profile?.section_id).catch(() => []),
          studentRequestService.listDepartmentNotifications(profile?.department_id, user.id, profile?.section_id).catch(() => []),
        ]);
        if (queue.length) {
          const latestQueueUpdate = queue[0]?.updated_at || queue[0]?.created_at || "unknown";
          derived.push({
            id: `derived:student-request-queue:${queue.length}:${latestQueueUpdate}`,
            title: "Student requests need attention",
            message: `${queue.length} student request${queue.length === 1 ? " is" : "s are"} waiting in your workflow queue.`,
            type: "action_required",
            actionUrl: "/student-requests",
            createdAt: queue[0]?.updated_at || queue[0]?.created_at,
          });
        }
        departmentNotes.forEach((note) => derived.push({
          id: `derived:department-note:${note.id}`,
          title: note.title || "Student request notification",
          message: note.message,
          type: "status_update",
          actionUrl: "/student-requests",
          createdAt: note.created_at,
        }));
      }

      if (hasPoeRole(roles)) {
        const isStudent = roles.includes("student");
        const isExternalVerifier = roles.includes("external_poe_verifier");
        const poeItems = await (isStudent
          ? poeService.getStudentDashboardItems()
          : isExternalVerifier
            ? poeService.getExternalDashboardItems()
            : poeService.getItems()).catch(() => []);
        const actionablePoeItems = (poeItems || []).filter((item) => (
          isStudent
            ? Boolean(item.can_upload)
            : isExternalVerifier
              ? Boolean(item.external_can_verify ?? item.can_verify) && !item.external_verified_at
              : Boolean(item.can_review)
        ));
        if (actionablePoeItems.length) {
          derived.push({
            id: "derived:poe-workflow-queue",
            title: isStudent ? "POE evidence is due" : "POE reviews need attention",
            message: isStudent
              ? `${actionablePoeItems.length} portfolio item${actionablePoeItems.length === 1 ? " is" : "s are"} ready for your upload.`
              : `${actionablePoeItems.length} portfolio item${actionablePoeItems.length === 1 ? " is" : "s are"} waiting for your action.`,
            type: "action_required",
            actionUrl: "/poe-management-hub",
            createdAt: actionablePoeItems[0]?.updated_at || actionablePoeItems[0]?.created_at,
          });
        }
      }

      const unique = new Map();
      [...generic.map(normalizeNotification), ...derived].forEach((item) => {
        const key = item.id || `${item.title}:${item.message}`;
        if (!dismissedNotificationIdsRef.current.has(key) && !unique.has(key)) unique.set(key, item);
      });
      setNotifications([...unique.values()].sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt)));
    } finally {
      setLoading(false);
    }
  }, [authLoading, profile, user]);

  React.useEffect(() => { void load(); }, [load, location.pathname]);

  React.useEffect(() => {
    if (!user?.id || authLoading) return undefined;
    const unsubscribe = notificationService.subscribe(user.id, () => { void load(); });
    const interval = window.setInterval(() => { void load(); }, 60000);
    return () => { unsubscribe(); window.clearInterval(interval); };
  }, [authLoading, load, user?.id]);

  const openNotification = async (notification) => {
    if (String(notification.id).startsWith("derived:")) {
      dismissedNotificationIdsRef.current.add(notification.id);
    }
    setNotifications((current) => current.filter((item) => item.id !== notification.id));
    await notificationService.markRead(notification.id, user?.id).catch(() => {});
    setOpen(false);
    navigate(notification.actionUrl || "/student-requests");
  };

  const clearNotifications = async () => {
    notifications.forEach((notification) => {
      if (String(notification.id).startsWith("derived:")) {
        dismissedNotificationIdsRef.current.add(notification.id);
      }
    });
    setNotifications([]);
    await notificationService.markAllRead(user?.id).catch(() => {});
    setOpen(false);
  };

  if (!user?.id || authLoading || (!notifications.length && !loading)) return null;

  const widgetPositionClass = position ? "" : "bottom-4 right-4 sm:bottom-6 sm:right-6";
  const panelAbove = !position || position.top > 340;
  const panelAlignLeft = position?.left < 360;

  return (
    <div
      ref={widgetRef}
      className={`fixed z-[300] ${widgetPositionClass}`}
      style={position ? { left: `${position.left}px`, top: `${position.top}px` } : undefined}
    >
      {open && (
        <div className={`absolute ${panelAbove ? "bottom-14" : "top-14"} ${panelAlignLeft ? "left-0 right-auto" : "right-0"} w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl`}>
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-bold text-slate-900">Notifications</p>
              <p className="text-xs text-slate-500">Items requiring your attention</p>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => void clearNotifications()} className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="Clear all notifications">Clear all</button>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close notifications"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="max-h-[min(28rem,70vh)] overflow-y-auto p-2">
            {notifications.map((notification) => (
              <button key={notification.id} type="button" onClick={() => void openNotification(notification)} className="flex w-full items-start gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50">
                <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${notification.type === "action_required" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                  {notification.type === "action_required" ? <Clock3 className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-900">{notification.title}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-slate-600">{notification.message}</span>
                  <span className="mt-1 block text-[11px] text-slate-400">{relativeTime(notification.createdAt)} · Open workflow</span>
                </span>
                <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          setOpen((value) => !value);
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        className="relative grid h-12 w-12 cursor-grab touch-none place-items-center rounded-full bg-primary text-primary-foreground shadow-xl ring-4 ring-white transition hover:scale-105 active:cursor-grabbing"
        aria-label={`Open ${notifications.length} notifications. Drag to move.`}
        title="Click to open notifications · Drag to move"
      >
        <Bell className="h-5 w-5" />
        <span className="absolute -right-1 -top-1 grid min-h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">{notifications.length > 99 ? "99+" : notifications.length}</span>
      </button>
    </div>
  );
};

export default FloatingNotificationWidget;
